import {ContentHeader} from '@components';
import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {FaEye, FaEyeSlash} from 'react-icons/fa';
import {useDispatch, useSelector} from 'react-redux';
import {useFormik} from 'formik';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import * as Yup from 'yup';
import {logoutUser} from '@app/store/reducers/auth';
import {toast} from 'react-toastify';
import {userSignup} from '../../services/user';
import {getUserPermission} from '../../utils/helpers';
import handleStatus from '../../utils/ajax';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .max(30, 'User Name must not exceed 30 characters')
    .matches(/^[A-Za-z\s]+$/, 'User Name must contain only letters and spaces')
    .required('User Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(5, 'Password must be at least 5 characters')
    .required('Password is required'),
  mobile: Yup.string()
    .required('Phone number is required')
    .matches(
      /^(\+\d{1,3}[- ]?)?\d{10,14}$/,
      'Phone number must be between 10 and 14 digits'
    ),
  gender: Yup.string()
    .oneOf(['Male', 'Female'], 'Invalid Gender')
    .required('Gender is required'),
  dob: Yup.date().required('Date of Birth is required'),
  // deactivated: Yup.boolean(),
  permissions: Yup.array().min(1, 'At least one permission is required'),
  designation: Yup.string().required('Designation is required'),
  emergencyContact: Yup.string()
    .required('Emergency contact number is required')
    .matches(
      /^(\+\d{1,3}[- ]?)?\d{10,14}$/,
      'Emergency contact number must be between 10 and 14 digits'
    ),
  address: Yup.string()
    .max(300, 'Address must not exceed 300 characters')
    .nullable()
});

const CreateUser = () => {
  const [value, setValue] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [emergencyContact, setEmergencyContact] = useState();
  const [styleForValidaiton] = useState({color: 'red'});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.auth.currentUser);

  const initialValues = {
    name: '',
    email: '',
    password: '',
    mobile: '',
    gender: '',
    dob: '',
    deactivated: 'false',
    permissions: [],
    designation: '',
    emergencyContact: '',
    address: ''
  };

  const onSubmit = async (values: any) => {
    try {
      // console.log(values, 'values while submitting kcsdjksdlsjl');
      // Normalize the 'deactivated' field
      if (values.deactivated === 'false') {
        values.deactivated = false;
      } else {
        values.deactivated = true;
      }

      // Attempt to sign up the user
      const resp = await userSignup(values);

      // Handle the response status
      handleStatus(resp.status, () => dispatch(logoutUser()), navigate);

      // Provide feedback upon successful user creation
      if (resp.status === 200) {
        toast.success('User Created Successfully');
        navigate('/all-users');
      }
    } catch (error) {
      // Display an error message to the user
      toast.error('An error occurred during user creation. Please try again.');
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema
  });

  const allowedRoles = ['superAdmin', 'admin', 'hr'];

  useEffect(() => {
    if (!allowedRoles.includes(getUserPermission(user))) {
      navigate('/'); // Redirect to home if not authorized
    }
  }, [user, navigate]);

  // Define available permissions
  const allPermissions = [
    {value: 'admin', label: 'Admin'},
    {value: 'business', label: 'Business'},
    {value: 'sales', label: 'Sales'},
    {value: 'hr', label: 'HR'},
    {value: 'seo', label: 'SEO'},
    {value: 'developer', label: 'Developer'}
  ];

  // Get current user's role
  const userRole = getUserPermission(user);

  // Filter permissions based on user role
  const filteredPermissions =
    userRole === 'hr'
      ? allPermissions.filter((perm) => perm.value !== 'admin')
      : allPermissions;

  return (
    <>
      <ContentHeader title="Create User" path="" />
      <section className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="card card-outline card-info">
              <div className="card-header">
                <h3 className="card-title">User Information</h3>
              </div>
              <div className="card-body">
                <div className="w-100">
                  <div className="card card-primary">
                    <form onSubmit={formik.handleSubmit}>
                      <div className="card-body d-flex flex-column gap-3">
                        <div className="row">
                          <label
                            htmlFor="inputName"
                            className="col-sm-2 col-form-label"
                          >
                            User Name
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              className="form-control"
                              id="inputName"
                              placeholder="Name"
                              name="name"
                              maxLength={30}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.name}
                            />
                            {formik.touched.name && formik.errors.name ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.name}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputEmail"
                            className="col-sm-2 col-form-label"
                          >
                            Email
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="email"
                              className="form-control"
                              id="inputEmail"
                              placeholder="Email"
                              name="email"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.email}
                            />
                            {formik.touched.email && formik.errors.email ? (
                              <div style={styleForValidaiton}>
                                {formik.errors.email}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="pexampleInputPassword1"
                            className="col-sm-2 col-form-label"
                          >
                            Password
                          </label>
                          <div className="col-sm-10 position-relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              className="form-control"
                              id="exampleInputPassword1"
                              placeholder="Password"
                              name="password"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.password}
                            />
                            <button
                              type="button"
                              style={{right: '5px', top: 0}}
                              className="btn position-absolute"
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                            {formik.touched.password &&
                            formik.errors.password ? (
                              <div style={styleForValidaiton}>
                                {formik.errors.password}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div
                          className="row"
                          style={{
                            justifyContent: 'space-between'
                            // alignItems: 'center'
                          }}
                        >
                          <label
                            htmlFor="inputName2"
                            style={{textAlign: 'left'}}
                          >
                            Mobile Number
                          </label>
                          <div className="col-sm-10">
                            <PhoneInput
                              defaultCountry="IN"
                              value={value}
                              onChange={(phone: any) => {
                                formik.values.mobile = phone;
                                setValue(phone);
                              }}
                              limitMaxLength
                              style={{
                                width: '100%',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                padding: '4px'
                                // outline: 'none'
                              }}
                            />
                            {formik.touched.mobile && formik.errors.mobile && (
                              <div style={{color: 'red', marginTop: '5px'}}>
                                {formik.errors.mobile}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Gender
                          </label>
                          <div className="col-sm-10">
                            <select
                              className="form-control"
                              name="gender"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.gender || ''}
                            >
                              <option value="">Select</option>{' '}
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>

                            {formik.touched.gender && formik.errors.gender ? (
                              <div style={styleForValidaiton}>
                                {formik.errors.gender}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Date of Birth
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="date"
                              name="dob"
                              className="form-control"
                              id="inputSkills"
                              placeholder="date"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.dob}
                            />
                            {formik.touched.dob && formik.errors.dob ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.dob}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Deactivated
                          </label>
                          <div className="col-sm-10">
                            <select
                              className="form-control"
                              name="deactivated"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.deactivated}
                            >
                              <option value="false">Activate</option>
                              <option value="true">Deactivate</option>
                            </select>
                            {formik.touched.deactivated &&
                            formik.errors.deactivated ? (
                              <div style={styleForValidaiton}>
                                {formik.errors.deactivated}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Designation
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              name="designation"
                              className="form-control"
                              id="inputPlace"
                              placeholder="Designation"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.designation}
                            />
                            {formik.touched.designation &&
                            formik.errors.designation ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.designation}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputPlace"
                            className="col-sm-2 col-form-label"
                          >
                            Permissions
                          </label>
                          <div className="col-sm-10">
                            <select
                              className="form-control"
                              name="permissions"
                              onChange={(event) => {
                                const selectedOptions = Array.from(
                                  event.target.selectedOptions,
                                  (option) => option.value
                                );
                                formik.setFieldValue(
                                  'permissions',
                                  selectedOptions
                                );
                              }}
                              onBlur={formik.handleBlur}
                              value={formik.values.permissions}
                            >
                              {filteredPermissions.map((perm) => (
                                <option key={perm.value} value={perm.value}>
                                  {perm.label}
                                </option>
                              ))}
                            </select>
                            {formik.touched.permissions &&
                            formik.errors.permissions ? (
                              <div style={styleForValidaiton}>
                                {formik.errors.permissions}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div
                          className="row"
                          style={{
                            justifyContent: 'space-between'
                            // alignItems: 'center'
                          }}
                        >
                          <label
                            htmlFor="inputName2"
                            style={{textAlign: 'left'}}
                          >
                            Emergency Number
                          </label>
                          <div className="col-sm-10">
                            <PhoneInput
                              defaultCountry="IN"
                              value={emergencyContact}
                              onChange={(emergencyContact: any) => {
                                formik.values.emergencyContact =
                                  emergencyContact;
                                setEmergencyContact(emergencyContact);
                              }}
                              limitMaxLength
                              style={{
                                width: '100%',
                                border: '1px solid #ced4da',
                                borderRadius: '4px',
                                padding: '4px'
                                // outline: 'none'
                              }}
                            />
                            {formik.touched.emergencyContact &&
                              formik.errors.emergencyContact && (
                                <div style={{color: 'red', marginTop: '5px'}}>
                                  {formik.errors.emergencyContact}
                                </div>
                              )}
                          </div>
                        </div>

                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Address
                          </label>
                          <div className="col-sm-10">
                            <textarea
                              className="form-control"
                              id="inputSkills"
                              name="address"
                              placeholder="Address"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.address}
                            />
                          </div>
                        </div>

                        <div>
                          <button
                            type="submit"
                            className="btn btn-primary offset-md-2"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CreateUser;
